import {
  Flex,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@kognitos/lattice';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import { ConfigFormRow } from '../WebhookTriggerDialog/ConfigFormRow';
import type { IIntegrationSelectRowProps } from './types';

/**
 * Shared integration select row for trigger configure steps.
 *
 * Renders the integration dropdown (with icons) inside a ConfigFormRow.
 * Translation strings are passed in so the row is namespace-agnostic.
 *
 * @param props - Component props
 * @returns JSX element containing the integration select row
 */
export function IntegrationSelectRow({
  label,
  description,
  placeholder,
  selectedIntegration,
  onIntegrationChange,
  uniqueIntegrations,
  isLoading,
  disabled = false,
}: IIntegrationSelectRowProps) {
  return (
    <ConfigFormRow label={label} description={description}>
      {isLoading ? (
        <Skeleton className="h-9 w-full rounded-md bg-alternate-1" />
      ) : (
        <Select
          value={selectedIntegration}
          onValueChange={onIntegrationChange}
          disabled={disabled}
        >
          <SelectTrigger className="w-full bg-background">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {uniqueIntegrations.map((integration: Bdkv2BookDescriptor) => (
              <SelectItem key={integration.name} value={integration.name ?? ''}>
                <Flex direction="row" align="center" gap={2}>
                  {integration.icon && (
                    <img
                      src={convertToDataUrl(integration.icon)}
                      alt={`${integration.display_name || integration.name} icon`}
                      className="w-4 h-4"
                    />
                  )}
                  {integration.display_name || integration.name}
                </Flex>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </ConfigFormRow>
  );
}
